/**
 * AnswerFeedbackPanel.jsx
 * ─────────────────────────────────────────────────────────────
 * IJ:       IJ-252 · Phase 5E-3d · Query Thread Render Extraction · ANSWER FEEDBACK sub-region
 * Date:     2026-08-02
 * Umbrella: IJ-076 · Predictive Text Rebuild continuation · Refactor Programme 2026 · Phase 5E-3
 * Owner:    E1
 *
 * Purpose:
 *   Extract the ANSWER FEEDBACK sub-region from the KnowledgeGraphPage.jsx
 *   queryThread render tree. Fourth slice of the five-sub-slice Phase 5E-3
 *   programme. Renders the thumbs-up/thumbs-down chip strip · a rating-recorded
 *   badge on submission · a root-cause dropdown that appears when thumbs-down
 *   is selected · a comment draft textarea with Send button.
 *
 * Guard: rendered only when item.query_id is present AND item.result.is_demo
 *        is falsy. The canned KG demo carries is_demo=true so the panel does
 *        not appear on demo results.
 *
 * Props (6):
 *   item                   · one queryThread entry (owns item.query_id ·
 *                             item.result.is_demo · item.query)
 *   idx                    · thread-item index (keys the data-testid)
 *   feedbackByQid          · from useAnswerFeedback hook · dictionary keyed by
 *                             item.query_id
 *   commentDraft           · from useAnswerFeedback hook · in-progress comment
 *                             text per query_id
 *   setCommentDraft        · from useAnswerFeedback hook · setter for the draft
 *   submitAnswerFeedback   · from useAnswerFeedback hook · async POST handler
 *
 * Byte-verbatim on every className · style · data-testid · axios URL ·
 * callback body. Zero behaviour change.
 *
 * The parent's IIFE wrapper (immediately-invoked-function-expression) unwraps
 * to an early-return guard plus plain const declarations · semantically
 * identical to the previous pattern.
 */
import React from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

export function AnswerFeedbackPanel({
  item,
  idx,
  feedbackByQid,
  commentDraft,
  setCommentDraft,
  submitAnswerFeedback,
}) {
  if (!item.query_id || item.result.is_demo) return null;
  const fb = feedbackByQid[item.query_id] || {};
  const rated = fb.rating === 1 || fb.rating === -1;
  const showCommentInput = fb.rating === -1;
  const draft = commentDraft[item.query_id] ?? '';
  return (
                  <div className="mt-4 mx-auto" style={{ maxWidth: '85%' }} data-testid={`answer-feedback-${idx}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-gray-400 uppercase tracking-wide">Was this helpful?</span>
                      <button
                        onClick={() => submitAnswerFeedback(item.query_id, item.query, 1, '')}
                        disabled={fb.submitting}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border disabled:opacity-50 ${
                          fb.rating === 1
                            ? 'bg-green-600 text-white border-green-500 shadow-[0_0_12px_rgba(34,197,94,0.45)]'
                            : 'bg-transparent text-green-400 border-green-500/40 hover:bg-green-600/15 hover:border-green-500'
                        }`}
                        data-testid={`feedback-thumbs-up-${idx}`}
                        aria-pressed={fb.rating === 1}
                        title="This answer was helpful"
                      >
                        <ThumbsUp className="h-7 w-7" />
                        {fb.rating === 1 ? 'Helpful' : 'Yes'}
                      </button>
                      <button
                        onClick={() => submitAnswerFeedback(item.query_id, item.query, -1, fb.comment || '')}
                        disabled={fb.submitting}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border disabled:opacity-50 ${
                          fb.rating === -1
                            ? 'bg-red-600 text-white border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.45)]'
                            : 'bg-transparent text-red-400 border-red-500/40 hover:bg-red-600/15 hover:border-red-500'
                        }`}
                        data-testid={`feedback-thumbs-down-${idx}`}
                        aria-pressed={fb.rating === -1}
                        title="This answer was not helpful"
                      >
                        <ThumbsDown className="h-7 w-7" />
                        {fb.rating === -1 ? 'Not helpful' : 'No'}
                      </button>
                      {rated && fb.rating === -1 && (
                        <span className="text-[11px] text-gray-500 italic">
                          Logged. Tell us why below if you can.
                        </span>
                      )}
                    </div>
                    {showCommentInput && (
                      <>
                      {/* Piece 2 — Root-cause attribution.
                          Decomposes 'not helpful' into 3 buckets the team can
                          actually route: agent misread / user changed mind /
                          engine quality. Optional — users can skip and just
                          leave a comment. */}
                      <div className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid={`root-cause-block-${idx}`}>
                        <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2 font-semibold">
                          What went wrong? <span className="text-gray-500 normal-case font-normal">(optional — helps us fix the right thing)</span>
                        </p>
                        <div className="flex flex-col gap-1.5">
                          {[
                            { key: 'intent_misread',  label: "I didn't get what I asked for",      sublabel: 'We misunderstood your query' },
                            { key: 'user_drift',      label: "I didn't know what I wanted",          sublabel: 'Turned out you wanted something else' },
                            { key: 'engine_quality',  label: 'Results themselves were poor',         sublabel: 'Sources/quality, not interpretation' },
                            { key: 'other',           label: 'Other (use comment box below)',        sublabel: null },
                          ].map(opt => (
                            <label
                              key={opt.key}
                              className={`flex items-start gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors border ${
                                fb.root_cause === opt.key
                                  ? 'bg-red-600/20 border-red-500/60'
                                  : 'border-transparent hover:bg-white/5'
                              }`}
                              data-testid={`root-cause-option-${opt.key}-${idx}`}
                            >
                              <input
                                type="radio"
                                name={`root-cause-${item.query_id}`}
                                value={opt.key}
                                checked={fb.root_cause === opt.key}
                                onChange={() => submitAnswerFeedback(item.query_id, item.query, -1, draft, opt.key)}
                                className="mt-0.5 accent-red-500"
                                data-testid={`root-cause-radio-${opt.key}-${idx}`}
                              />
                              <span className="flex-1">
                                <span className="block text-xs text-gray-200">{opt.label}</span>
                                {opt.sublabel && <span className="block text-[10px] text-gray-500">{opt.sublabel}</span>}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="mt-2 flex items-start gap-2" data-testid={`feedback-comment-block-${idx}`}>
                        <textarea
                          value={draft}
                          onChange={(e) => setCommentDraft(prev => ({ ...prev, [item.query_id]: e.target.value }))}
                          placeholder="What was wrong, missing, or unhelpful? (optional, max 500 chars)"
                          maxLength={500}
                          rows={2}
                          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-200 text-xs placeholder-gray-500 focus:outline-none focus:border-red-400 resize-none"
                          data-testid={`feedback-comment-${idx}`}
                        />
                        <button
                          onClick={() => submitAnswerFeedback(item.query_id, item.query, -1, draft, fb.root_cause)}
                          disabled={fb.submitting}
                          className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-semibold transition-colors disabled:opacity-50 self-stretch"
                          data-testid={`feedback-comment-submit-${idx}`}
                        >
                          {fb.submitting ? 'Saving...' : 'Send'}
                        </button>
                      </div>
                      </>
                    )}
                  </div>
  );
}
